import { useState, useEffect, useRef, useCallback } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// KyberCosplay talks directly to the shared Star Forge chat backend (the same
// service that powers thestarforge.org and MandoMaster), tagging its
// conversations with source='kybercosplay' so they land in KyberCosplay's own
// Discord forum channel.
const SUPPORT_URL = process.env.REACT_APP_SUPPORT_URL || 'https://sfchat.kybersuite.com';
const WS_URL = SUPPORT_URL.replace(/^http/, 'ws');

const CID_KEY = 'kc_support_cid';
const NAME_KEY = 'kc_support_name';
const EMAIL_KEY = 'kc_support_email';

const ALLOWED_UPLOAD_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
const attUrl = (u) => (u.startsWith('http') ? u : `${SUPPORT_URL}${u}`);

const TOPICS = [
  { key: 'Bug Report', emoji: '🐞', label: 'Report a bug', hint: "Something not working right? Tell us what happened." },
  { key: 'Question', emoji: '💬', label: 'Ask a question', hint: 'Need help or have a how-to question?' },
  { key: 'Feature Request', emoji: '✨', label: 'Request a feature', hint: "Want KyberCosplay to do something it doesn't yet? Let us know." },
];

function greetingFor(topic) {
  if (topic === 'Bug Report') return "Thanks for helping us improve KyberCosplay. Tell us what happened and what you expected, and we'll look into it.";
  if (topic === 'Question') return "Happy to help. Ask your question below and we'll get back to you.";
  if (topic === 'Feature Request') return "Love the ideas. Tell us what you'd like KyberCosplay to do and we'll consider it.";
  return 'Hi! How can we help? Send us a message below.';
}

const INPUT = 'w-full bg-slate-950/50 border border-white/10 rounded-xl px-3 py-2 text-sm text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-cyan-500/40 transition-colors';

export default function SupportWidget({ open, onClose }) {
  const { user } = useAuth();

  const [screen, setScreen] = useState('intent'); // intent | form | chat | ended
  const [topic, setTopic] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [wantsTranscript, setWantsTranscript] = useState(true);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [endReason, setEndReason] = useState(null); // 'self' | 'operator'
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const cidRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const reconnectDelayRef = useRef(1000);
  const firstSentRef = useRef(false);
  const endedRef = useRef(false);
  const screenRef = useRef(screen);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => { screenRef.current = screen; }, [screen]);

  // Prefill from the signed-in account (without clobbering edits in progress)
  useEffect(() => {
    if (!user) return;
    setEmail((e) => e || user.email || '');
    setName((n) => n || user.display_name || '');
  }, [user]);

  // Auto-scroll the message list
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, screen]);

  const appendMsg = useCallback((role, text, sentAt, attachments) => {
    setMessages((prev) => [...prev, { role, text, sentAt, attachments }]);
  }, []);

  const clearSession = useCallback(() => {
    localStorage.removeItem(CID_KEY);
    localStorage.removeItem(NAME_KEY);
    localStorage.removeItem(EMAIL_KEY);
    cidRef.current = null;
  }, []);

  const connectWs = useCallback(() => {
    const cid = cidRef.current;
    if (!cid) return;
    const ws = new WebSocket(`${WS_URL}?cid=${cid}`);
    wsRef.current = ws;

    ws.addEventListener('open', () => {
      reconnectDelayRef.current = 1000;
      setConnected(true);
    });

    ws.addEventListener('message', (ev) => {
      try {
        const m = JSON.parse(typeof ev.data === 'string' ? ev.data : '');
        if (m.type === 'operator_message' && (m.text || m.attachments?.length)) {
          appendMsg('operator', m.text ?? '', undefined, m.attachments);
        } else if (m.type === 'rate_limited') {
          setError("You're sending messages too quickly. Give it a moment.");
        } else if (m.type === 'chat_ended') {
          endedRef.current = true;
          clearSession();
          setEndReason('operator');
          setScreen('ended');
          wsRef.current?.close(1000, 'ended by operator');
        }
      } catch {
        /* ignore malformed frames */
      }
    });

    ws.addEventListener('close', (ev) => {
      wsRef.current = null;
      setConnected(false);
      if (ev.code !== 4001 && !endedRef.current && cidRef.current && screenRef.current === 'chat') {
        reconnectRef.current = setTimeout(() => {
          reconnectDelayRef.current = Math.min(reconnectDelayRef.current * 2, 30000);
          connectWs();
        }, reconnectDelayRef.current);
      }
    });

    ws.addEventListener('error', () => ws.close());
  }, [appendMsg, clearSession]);

  const loadHistory = useCallback(async (cid) => {
    try {
      const res = await fetch(`${SUPPORT_URL}/api/conversations/${cid}`);
      if (res.status === 404) return 'gone';
      const data = await res.json();
      const restored = (data.messages ?? [])
        .filter((m) => m.role === 'visitor' || m.role === 'operator')
        .map((m) => ({ role: m.role, text: m.text, sentAt: new Date(m.sentAt), attachments: m.attachments }));
      setMessages(restored);
      return data.status === 'ended' ? 'ended' : 'ok';
    } catch {
      return 'ok';
    }
  }, []);

  // Restore an in-progress conversation the first time the panel opens
  useEffect(() => {
    if (!open) return;
    if (screenRef.current !== 'intent') return;
    const storedCid = localStorage.getItem(CID_KEY);
    if (!storedCid) return;

    cidRef.current = storedCid;
    setName(localStorage.getItem(NAME_KEY) ?? '');
    setEmail(localStorage.getItem(EMAIL_KEY) ?? '');
    firstSentRef.current = true;
    setScreen('chat');
    void loadHistory(storedCid).then((result) => {
      if (result === 'gone') { clearSession(); setScreen('intent'); return; }
      if (result === 'ended') { setScreen('ended'); return; }
      connectWs();
    });
  }, [open, loadHistory, connectWs, clearSession]);

  // Close the socket on tab navigation so the backend can fall back to email
  useEffect(() => {
    const handler = () => {
      if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.close(1000, 'navigation');
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // Cleanup timers/socket on unmount
  useEffect(() => () => {
    if (reconnectRef.current) clearTimeout(reconnectRef.current);
    wsRef.current?.close();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError('Email is required.'); return; }
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${SUPPORT_URL}/api/conversations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          visitorName: name.trim() || email.trim().split('@')[0],
          email: email.trim(),
          wantsTranscript,
          source: 'kybercosplay',
        }),
      });
      if (res.status === 429) { setError('Too many chats started. Please try again in a few minutes.'); return; }
      if (!res.ok) { setError('Could not start the chat. Please try again.'); return; }
      const data = await res.json();
      cidRef.current = data.conversationId;
      localStorage.setItem(CID_KEY, data.conversationId);
      localStorage.setItem(NAME_KEY, name.trim());
      localStorage.setItem(EMAIL_KEY, email.trim());
      firstSentRef.current = false;
      endedRef.current = false;
      setEndReason(null);
      setMessages([{ role: 'operator', text: greetingFor(topic) }]);
      setScreen('chat');
      connectWs();
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const sendMessage = () => {
    const text = draft.trim();
    const ws = wsRef.current;
    if (!text || !ws || ws.readyState !== WebSocket.OPEN) return;
    let outgoing = text;
    if (!firstSentRef.current && topic) {
      outgoing = `[${topic}] ${text}`;
      firstSentRef.current = true;
    }
    ws.send(JSON.stringify({ type: 'visitor_message', text: outgoing }));
    appendMsg('visitor', text);
    setDraft('');
  };

  const handleFile = (file) => {
    setError('');
    const cid = cidRef.current;
    if (!cid) return;
    if (!ALLOWED_UPLOAD_TYPES.includes(file.type)) { setError('Only PNG, JPEG, or WebP images are allowed.'); return; }
    if (file.size > MAX_UPLOAD_BYTES) { setError('Image is too large (max 10MB).'); return; }
    void uploadImage(cid, file);
  };

  const uploadImage = async (cid, file) => {
    setUploading(true);
    const form = new FormData();
    form.append('file', file, file.name);
    try {
      const res = await fetch(`${SUPPORT_URL}/api/conversations/${cid}/attachments`, { method: 'POST', body: form });
      if (res.status === 201) {
        const data = await res.json();
        appendMsg('visitor', '', new Date(), [data.attachment]);
      } else if (res.status === 413) setError('Image is too large (max 10MB).');
      else if (res.status === 415) setError('Only PNG, JPEG, or WebP images are allowed.');
      else if (res.status === 429) setError('Too many uploads. Please wait a moment and try again.');
      else if (res.status === 409) setError('This chat has ended.');
      else setError('Could not send the image. Please try again.');
    } catch {
      setError('Could not send the image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const endChat = () => {
    endedRef.current = true;
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'end_chat' }));
      ws.close(1000, 'ended');
    }
    if (reconnectRef.current) clearTimeout(reconnectRef.current);
    clearSession();
    setEndReason('self');
    setScreen('ended');
  };

  const resetToIntent = () => {
    clearSession();
    setMessages([]);
    setDraft('');
    setTopic('');
    setError('');
    setEndReason(null);
    firstSentRef.current = false;
    endedRef.current = false;
    setScreen('intent');
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed z-50 inset-x-3 bottom-3 sm:inset-x-auto sm:right-4 sm:bottom-4 sm:w-[380px] h-[72vh] sm:h-[540px] sm:max-h-[80vh] flex flex-col bg-slate-900/95 backdrop-blur-xl rounded-2xl border border-white/10 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.8)]" />
            <h2 className="text-sm font-heading font-bold uppercase tracking-widest text-cyan-300">Support</h2>
          </div>
          <div className="flex items-center gap-3">
            {screen === 'chat' && (
              <button onClick={endChat} className="text-[10px] uppercase tracking-wider text-slate-500 hover:text-red-400 transition-colors">End chat</button>
            )}
            <button onClick={onClose} aria-label="Close support" className="text-slate-400 hover:text-white transition-colors"><X className="w-4 h-4" /></button>
          </div>
        </div>

        {/* Intent */}
        {screen === 'intent' && (
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            <p className="text-xs text-slate-400">How can we help?</p>
            {TOPICS.map((t) => (
              <button key={t.key} onClick={() => { setTopic(t.key); setError(''); setScreen('form'); }}
                className="glass-card w-full text-left rounded-xl px-4 py-3 hover:border-cyan-500/40 transition-colors">
                <div className="text-sm text-slate-100 font-medium">{t.emoji} {t.label}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{t.hint}</div>
              </button>
            ))}
          </div>
        )}

        {/* Form */}
        {screen === 'form' && (
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-3">
            <button type="button" onClick={() => setScreen('intent')} className="text-[10px] text-slate-500 hover:text-cyan-300 transition-colors">← Back</button>
            <p className="text-xs font-semibold uppercase tracking-wider text-cyan-300">{TOPICS.find((t) => t.key === topic)?.label || 'Contact support'}</p>

            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-widest text-slate-500">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" placeholder="you@example.com" required className={INPUT} />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-widest text-slate-500">Name <span className="opacity-60">(optional)</span></label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" placeholder="Your name" maxLength={80} className={INPUT} />
            </div>
            <label className="flex items-center gap-2 text-[11px] text-slate-400 cursor-pointer">
              <input type="checkbox" checked={wantsTranscript} onChange={(e) => setWantsTranscript(e.target.checked)} className="accent-cyan-500" />
              Email me a transcript of this chat
            </label>

            {error && <p className="text-xs text-red-400">{error}</p>}

            <button type="submit" disabled={submitting || !email.trim()}
              className="w-full py-2 rounded-full bg-cyan-500 hover:bg-cyan-400 text-white text-xs font-semibold tracking-wider uppercase transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
              {submitting ? 'Starting…' : 'Start chat'}
            </button>
            <p className="text-[10px] leading-relaxed text-slate-500 opacity-80">Your email is only used so we can reply to you. We don't sell or share it.</p>
          </form>
        )}

        {/* Chat */}
        {screen === 'chat' && (
          <>
            <div ref={scrollRef}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) handleFile(f); }}
              className={`flex-1 overflow-y-auto p-4 space-y-2 min-h-0 ${dragOver ? 'ring-2 ring-inset ring-cyan-400' : ''}`}>
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'visitor' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-xl px-3 py-2 text-xs leading-relaxed ${m.role === 'visitor' ? 'bg-cyan-500/15 text-slate-100 border border-cyan-500/30' : 'bg-slate-800/60 text-slate-100 border border-white/10'}`}>
                    {m.text && <div className="whitespace-pre-wrap break-words">{m.text}</div>}
                    {m.attachments?.map((a, j) => (
                      <img key={j} src={attUrl(a.url)} alt={a.name || 'image'} loading="lazy"
                        onClick={() => setLightbox(attUrl(a.url))}
                        className={`block max-h-52 rounded-md cursor-pointer border border-white/10 ${m.text ? 'mt-1.5' : ''}`} />
                    ))}
                  </div>
                </div>
              ))}
              {uploading && <p className="text-[10px] text-center text-slate-500 animate-pulse">Uploading image…</p>}
              {!connected && <p className="text-[10px] text-center text-slate-500 animate-pulse">Connecting…</p>}
            </div>

            {error && <p className="px-4 pb-1 text-[10px] text-red-400">{error}</p>}

            <div className="border-t border-white/10 p-2 flex gap-2 shrink-0">
              <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" hidden
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }} />
              <button type="button" onClick={() => fileInputRef.current?.click()} disabled={!connected || uploading}
                aria-label="Attach image" title="Attach an image"
                className="px-3 text-base rounded-xl border border-white/10 text-cyan-300 hover:bg-cyan-500/10 hover:border-cyan-500/40 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">📎</button>
              <input value={draft} onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                onPaste={(e) => { const item = Array.from(e.clipboardData.items).find((it) => it.type.startsWith('image/')); const f = item?.getAsFile(); if (f) { e.preventDefault(); handleFile(f); } }}
                placeholder="Type your message…" className={`flex-1 ${INPUT}`} />
              <button onClick={sendMessage} disabled={!connected || !draft.trim()}
                className="px-4 text-xs rounded-xl border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed uppercase tracking-wider">Send</button>
            </div>
          </>
        )}

        {/* Ended */}
        {screen === 'ended' && (
          <div className="flex-1 flex flex-col items-center justify-center p-6 text-center space-y-3">
            <div className="text-3xl">{endReason === 'operator' ? '🔒' : '✓'}</div>
            <p className="text-sm text-slate-100 font-medium">{endReason === 'operator' ? 'Chat has been ended' : "Thanks, we'll be in touch!"}</p>
            {endReason === 'operator' && <p className="text-xs text-slate-400">Our team closed this conversation.</p>}
            {endReason === 'operator'
              ? (email && wantsTranscript && <p className="text-xs text-slate-400">A transcript was sent to <span className="text-cyan-300">{email}</span>.</p>)
              : (email && <p className="text-xs text-slate-400">Any replies will go to <span className="text-cyan-300">{email}</span>.</p>)}
            <button onClick={resetToIntent} className="mt-2 px-4 py-2 rounded-full border border-white/10 text-xs text-slate-400 hover:border-cyan-500/40 hover:text-cyan-300 transition-colors uppercase tracking-wider">Start a new chat</button>
          </div>
        )}
      </div>

      {lightbox && (
        <div onClick={() => setLightbox(null)} className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-6 cursor-zoom-out">
          <img src={lightbox} alt="" className="max-w-full max-h-full rounded-lg shadow-2xl" />
        </div>
      )}
    </>
  );
}
